import React, { useRef, useState } from "react";
import "./ModernLoginPage.css";
import logoIcon from "../../images/icons/logo.png";
import { useNavigate } from "react-router-dom";
import { APICall } from "../../services/APIService";
import {
  API_LOGIN_URL,
  APILoginType,
} from "../../services/APIAuthorizationService";
import { generatePKCE } from "../../utils/keycloakPKCEUtils";
import AlertDialog from "../../components/popupComp/AlertDialog";
import { EyeIcon, EyeOffIcon } from "../../components/iconSet/NewIconSet";

const LoginPageNew2 = () => {
  const navigate = useNavigate();
  const usernameRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [alertDialogState, setAlertDialogState] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [alertCallback, setAlertCallback] = useState<(() => void) | undefined>();
  const videoRef = useRef<HTMLVideoElement>(null);

      const [error, setError] = useState<string>('')
      const [success, setSuccess] = useState<string>('')

  const showAlertDialog = (
    title: string,
    description: string,
    callback?: () => void,
  ) => {
    setAlertTitle(title);
    setAlertDescription(description);
    setAlertCallback(() => callback);
    setAlertDialogState(true);
  };

  const handleLoginButtonClick = async (e: React.FormEvent) => {
    e.preventDefault();

    if (usernameRef.current && passwordRef.current) {
      try {
        setIsAuthenticating(true);
        const codeChallenge = await generatePKCE();

        const requestBody: APILoginType = {
          username: usernameRef.current.value,
          password: passwordRef.current.value,
          codeChallenge,
        };

        const response = await APICall(API_LOGIN_URL, "POST", requestBody);

        if (response.access_token) {
          localStorage.setItem("access_token", response.access_token);
          localStorage.setItem("refresh_token", response.refresh_token);
          sessionStorage.setItem("access_token", response.access_token);
          sessionStorage.setItem("refresh_token", response.refresh_token);

          navigate("/callback", {
            state: response,
          });
        } else {
          //showAlertDialog("Login Failed", "Invalid credentials");
          setError("Username or password incorrect");
        }
      } catch (error: any) {
        const backendDetails = error?.details;
        const backendError = error?.message;

        if (error instanceof Error && error.message === "Access denied") {
          //showAlertDialog("Login Failed", "Access denied");
          setError("Access denied");
        } else if (backendDetails === "Account is inactive") {
          //showAlertDialog("Login Blocked", backendError);
          setError("Account is inactive");
        } else {
          //showAlertDialog("Login Failed", "Username or password incorrect");
          setError("Username or password incorrect");
        }
      } finally {
        setIsAuthenticating(false);
      }
    }
  };

  const handleNavigateForgotPassword = () => {
    navigate("/forgotpassword");
  };

  const handleVideoLoaded = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 110;
    }
  };

  const handleVideoTimeUpdate = () => {
    if (videoRef.current && videoRef.current.currentTime >= 130) {
      videoRef.current.currentTime = 110;
    }
  };

  /*
  const handleVideoLoaded = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleVideoTimeUpdate = () => {
    if (videoRef.current && videoRef.current.currentTime >= 4) {
      videoRef.current.currentTime = 0;
    }
  }; 
   */

  return (
    <div className="modern-login-root">
        <video autoPlay muted playsInline className="background-video" loop>
      {/* <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="background-video"
        //onLoadedMetadata={handleVideoLoaded}
        //onTimeUpdate={handleVideoTimeUpdate}
      > */}
        <source src="/MV_FVM_Video.mp4" type="video/mp4" />
      </video>

      <div className="video-overlay" />

      <div className="login-section">
        <div className="login-card">
          <div className="brand-area">
            <img src={logoIcon} alt="BCONX" className="brand-logo" />
            <h1>BCONX</h1>
            <p>
              Backoffice System
            </p>
          </div>

          <form onSubmit={handleLoginButtonClick}>
            <div className="inputGroup">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                placeholder="Enter username"
                ref={usernameRef}
                required
                autoComplete="username"
              />
            </div>

            <div className="inputGroup">
              <label htmlFor="password">Password</label>
              <div className="password-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  ref={passwordRef}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>

            {error && (
                  <div className={'errorMessage'} role="alert">
                      {error}
                  </div>
              )}

              {success && (
                  <div className={'successMessage'} role="status">
                      {success}
                  </div>
              )}

            <div className="login-options">
              {/* <label className="remember-box">
                <input type="checkbox" />
                <span>Remember me</span>
              </label> */}
              <button
                type="button"
                className="forgot-btn"
                onClick={handleNavigateForgotPassword}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className="signin-btn"
              disabled={isAuthenticating}
            >
              {isAuthenticating ? "Authenticating..." : "Login"}
            </button>
          </form>
        </div>
      </div>

      <div className="info-section">
        <div className="info-content">
          <span className="portal-badge">EARLY WARNING PLATFORM</span>
          <h2>
            BCONX: Integrated Early Warning
            <br />
            Management System
          </h2>
          <p>
            BCONX is an integrated early warning management application designed to support timely and effective dissemination of alerts across multiple hazards. The system enables organizations to manage workflows, generate early warning messages, and ensure critical information reaches communities through SMS, Viber, WhatsApp, and CAP.
          </p>

          <div className="feature-list">
            <FeatureCard
              title="Workflow Setup"
              desc="Create and configure workflows that define the sequence of actions and approvals required before an early warning is triggered."
            />
            <FeatureCard
              title="Early Warning Trigger"
              desc="Design and initiate early warning messages using predefined templates or localized formats."
            />
            <FeatureCard
              title="Message Approval & Rejection"
              desc="Facilitate a structured review and approval process to ensure message accuracy and authorization."
            />
            <FeatureCard
              title="Recipient Management"
              desc="Define and manage recipients to ensure the right individuals and communities receive timely alerts."
            />
            <FeatureCard
              title="Acknowledgement Tracking"
              desc="Monitor responses and acknowledgements from recipients to assess communication reach and effectiveness."
            />
            <FeatureCard
              title="Early Warning Template Management"
              desc="Create and maintain early warning message templates for consistent and rapid alert generation."
            />
            <FeatureCard
              title="Reported Incidents Management"
              desc="Review, approve, or reject incidents reported by staff or guest users through the Udha application, ensuring only verified events trigger early warnings."
            />
          </div>
        </div>
      </div>

      {alertDialogState && (
        <AlertDialog
          title={alertTitle}
          description={alertDescription}
          windowState={alertDialogState}
          setWindowState={setAlertDialogState}
          function={alertCallback}
        />
      )}
    </div>
  );
};

const FeatureCard = ({ title, desc }: { title: string; desc: string }) => (
  <div className="feature-card">
    <div className="feature-icon" />
    <div>
      <h4>{title}</h4>
      <p>{desc}</p>
    </div>
  </div>
);

export default LoginPageNew2;